import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';

import { useTheme } from '../../../../contexts/ThemeContext';
import type { TrailHeadMarkerData } from '../types';

/**
 * TrailHeadMarkerNode — small chrome chip pinned above the spine root.
 *
 * Renders compact: a 🥾 emoji + "Trail head" label + small caret pointing
 * down. Identifies the start of the trail regardless of whether the root
 * is a user turn_marker (real chat), an orphan-asst llm_round (persona
 * seed content), or a spine_root_marker (council peer chain entry).
 *
 * Synthetic decoration emitted by layoutGroups.decorationPass. No
 * incoming/outgoing edges; layout positions relative to the spine root.
 */
const TrailHeadMarkerNode: React.FC<NodeProps<TrailHeadMarkerData>> = () => {
  const { darkMode } = useTheme();

  return (
    <div
      className={`nodrag flex items-center gap-2 px-3 py-2 rounded-lg border-2 select-none shadow-md ${
        darkMode
          ? 'bg-amber-900/40 border-amber-400/70 text-amber-100'
          : 'bg-amber-100 border-amber-500 text-amber-950'
      }`}
      style={{ minWidth: 180 }}
      data-testid="trail-head-marker"
    >
      <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0 }} />
      <span aria-hidden style={{ fontSize: 18, lineHeight: 1 }}>🥾</span>
      <div className="flex flex-col leading-tight" style={{ flex: 1 }}>
        <span
          className="font-bold uppercase tracking-wider text-[10px]"
          style={{ letterSpacing: '0.08em' }}
        >
          Trail Head
        </span>
        <span
          className={`text-[9px] italic ${
            darkMode ? 'text-amber-300/80' : 'text-amber-800/80'
          }`}
        >
          start of the trail
        </span>
      </div>
      <span
        aria-hidden
        className={`text-[16px] font-bold ${
          darkMode ? 'text-amber-300' : 'text-amber-700'
        }`}
      >
        ↓
      </span>
      <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

export default TrailHeadMarkerNode;
